import { CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Fragment, useEffect, useRef } from 'react';
import { ChatMessage } from '@/feature/chatbot/types';
import { MessageBubble } from './components/MessageBubble';
import {
  RateLastResponse,
  useRateLastResponse,
} from './components/rating/RateLastResponse';
import { ReferenceCards } from './components/references/ReferenceCards';

const MessagesContainer = styled('div')({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  overflow: 'hidden',
});

const MessagesScrollArea = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(2, 0),
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const LoadingIndicator = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

interface MessagesProps {
  messages: ChatMessage[];
  isLoading?: boolean;
}

export const Messages = ({ messages, isLoading = false }: MessagesProps) => {
  const { scrollAreaRef } = useScrollEffect({ isLoading });
  const { lastResponse, viewRating } = useRateLastResponse();

  return (
    <MessagesContainer>
      <MessagesScrollArea ref={scrollAreaRef}>
        {messages.map((message) => (
          <Fragment key={message.id}>
            {message.isReference ? (
              <ReferenceCards references={message.references} />
            ) : (
              <MessageBubble message={message} />
            )}
            {message.id === lastResponse?.id && viewRating && !isLoading && (
              <RateLastResponse />
            )}
          </Fragment>
        ))}
        {isLoading && !messages.some((msg) => msg.isStreaming) && (
          <LoadingIndicator>
            <CircularProgress size={24} />
          </LoadingIndicator>
        )}
      </MessagesScrollArea>
    </MessagesContainer>
  );
};

const useScrollEffect = ({ isLoading }: { isLoading: boolean }) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollAreaRef.current && isLoading) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [isLoading]);

  return { scrollAreaRef };
};
